"use client"

import React, { useState, useMemo, useEffect } from "react"
import { Blade } from "@/components/blades/blade"
import { ToolHeader, ToolbarPill } from "@/components/hub/hub-canvas"
import { Boxes, FlaskConical, Braces, Save } from "lucide-react"
import { toast } from "sonner"
import { TestingTab } from "@/components/tabs/testing-tab"
import { EntityManagementTab } from "@/components/tabs/entity-management-tab"
import { JsonTab } from "@/components/tabs/json-tab"
import { DestinationFieldMappingsTab } from "@/components/tabs/destination-field-mappings-tab"
import type { MappingConfig, AtsDestinationFieldInfo } from "@/types/fbo-api"
import type { FlatMapping } from "@/types/ui"
import { useMappingConfig } from "@/hooks/use-mapping-config"
import { useCode } from "@/contexts/code-context"
import { useAtsSource } from "@/contexts/ats-source-context"
import { useAtsInfo } from "@/hooks/use-ats-info"
import { validateMapping } from "@/lib/mapping-validation"
import { MappingFlowBlade } from "@/components/mapping-flow/mapping-flow-blade"
import { Waypoints } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface FieldMappingEditorProps {
  mappingName: string
}

export function FieldMappingEditor({ mappingName }: FieldMappingEditorProps) {
  const { t, n } = useTranslation()
  const { isLoading: codeValuesLoading, error: codeValuesError } = useCode()
  const { selectedSource } = useAtsSource()
  const { loadConfig, loadAvailableDestinationFields, saveConfig } = useMappingConfig()
  const [config, setConfig] = useState<MappingConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [availableDestinationFields, setAvailableDestinationFields] = useState<AtsDestinationFieldInfo[]>([])
  const [hasLoadedDestinationFields, setHasLoadedDestinationFields] = useState(false)

  // Load ATS info when config is available AND code values are loaded
  const atsInfoState = useAtsInfo((!codeValuesLoading && config?.atsSource) || null)

  const [flowOpen, setFlowOpen] = useState(false)
  const [panel, setPanel] = useState<"entities" | "testing" | "json" | null>(null)

  // Test form state (persists across config changes)
  const [testForm, setTestForm] = useState({
    primaryEntityId: "",
    entityType: "",
    entityId: "",
    sourceField: ""
  })

  // Load config on mount
  useEffect(() => {
    const loadInitialConfig = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const initialConfig = await loadConfig(mappingName, selectedSource?.code)
        setConfig(initialConfig)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t("mappingEditor.errors.loadFailed")
        setError(errorMessage)
        toast.error(t("mappingEditor.toasts.loadFailedTitle"), {
          description: t("mappingEditor.toasts.loadFailed"),
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadInitialConfig()
    // Re-load when the user switches ATS source — configs are per source
  }, [mappingName, selectedSource?.code])

  // Load destination fields on mount
  useEffect(() => {
    const loadDestinationFields = async () => {
      if (!hasLoadedDestinationFields) {
        try {
          const fieldInfos = await loadAvailableDestinationFields(mappingName)
          setAvailableDestinationFields(fieldInfos)
          setHasLoadedDestinationFields(true)
        } catch (err) {
          console.error('Failed to load destination fields:', err)
          setHasLoadedDestinationFields(true)
        }
      }
    }
    
    loadDestinationFields()
  }, [mappingName])

  // Create a flattened list of ALL destination fields (mapped + unmapped) for unified display
  const allMappings = useMemo(() => {
    if (!config) return []
    
    // First, create map of mapped fields
    const mappedFieldsMap = new Map<string, FlatMapping>()
    config.entities.forEach((entity, entityIndex) => {
      entity.fieldMappings.forEach((mapping, mappingIndex) => {
        const fieldInfo = availableDestinationFields.find(field => field.FieldPath === mapping.destinationField)
        const validation = validateMapping(
          mapping.destinationField,
          mapping.dataTypeConverter,
          availableDestinationFields,
          atsInfoState
        )
        
        mappedFieldsMap.set(mapping.destinationField, {
          destinationField: mapping.destinationField,
          sourceField: mapping.sourceField,
          entityIndex,
          entityName: entity.name,
          mappingIndex,
          valueMappings: mapping.valueMappings,
          valueMappingSetName: mapping.valueMappingSetName,
          dataTypeConverter: mapping.dataTypeConverter,
          defaultValueMapping: mapping.defaultValueMapping,
          isInvalid: validation.hasAnyInvalidation,
          validationIssues: {
            invalidDestinationField: !validation.isValidDestinationField,
            invalidDataTypeConverter: !validation.isValidDataTypeConverter,
          },
          codeTableName: fieldInfo?.CodeTable?.Name || null,
          codeTableValueType: fieldInfo?.CodeTable?.ValueType ?? null,
          isMapped: true,
        })
      })
    })

    // Get all mapped fields (including invalid/renamed ones)
    const mappedFields = Array.from(mappedFieldsMap.values())

    // Get unmapped available destination fields 
    const unmappedFields = availableDestinationFields
      .filter(fieldInfo => !mappedFieldsMap.has(fieldInfo.FieldPath))
      .map(fieldInfo => ({
        destinationField: fieldInfo.FieldPath,
        sourceField: null,
        entityIndex: null,
        entityName: null,
        mappingIndex: null,
        isInvalid: false,
        codeTableName: fieldInfo.CodeTable?.Name || null,
        codeTableValueType: fieldInfo.CodeTable?.ValueType ?? null,
        isMapped: false,
      }))

    // Concatenate mapped and unmapped fields
    const allFields = [...mappedFields, ...unmappedFields]

    return allFields.sort((a, b) => a.destinationField.localeCompare(b.destinationField))
  }, [config, availableDestinationFields, atsInfoState])

  // Check if there are any invalid destination fields (only check mapped fields)
  const hasInvalidMappings = useMemo(() => {
    return allMappings.some((mapping) => mapping.isMapped && mapping.isInvalid)
  }, [allMappings])

  // Get unique entity types for testing
  const entityTypes = useMemo(() => {
    if (!config) return []
    return [...new Set(config.entities.map((entity) => entity.entityType))]
  }, [config])

  // Don't render the page until code values are loaded
  if (codeValuesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2">{t("common.state.loading")}</p>
        </div>
      </div>
    )
  }

  // Show error if code values failed to load
  if (codeValuesError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600">{t("mappingEditor.errors.appDataFailed")}</p>
          <p className="text-gray-600 mt-2">{codeValuesError}</p>
        </div>
      </div>
    )
  }

  const handleSave = async () => {
    if (hasInvalidMappings) {
      toast.error(t("mappingEditor.toasts.cannotSaveTitle"), {
        description: t("mappingEditor.toasts.cannotSave"),
      })
      return
    }

    setIsLoading(true)
    setError(null)
    
    const success = await saveConfig(mappingName, config!, selectedSource?.code)
    if (success) {
      // Reload config after successful save
      try {
        const updatedConfig = await loadConfig(mappingName, selectedSource?.code)
        setConfig(updatedConfig)
        toast.success(t("mappingEditor.toasts.saveSuccessfulTitle"), {
          description: t("mappingEditor.toasts.saveSuccessful"),
        })
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t("mappingEditor.errors.reloadFailed")
        setError(errorMessage)
        toast.error(t("mappingEditor.toasts.saveReloadFailedTitle"), {
          description: t("mappingEditor.toasts.saveReloadFailed"),
        })
      }
    } else {
      const errorMessage = t("mappingEditor.toasts.saveFailed")
      setError(errorMessage)
      toast.error(t("mappingEditor.toasts.saveFailedTitle"), {
        description: errorMessage,
      })
    }
    
    setIsLoading(false)
  }

  // Show loading state
  if (isLoading && (!config || !hasLoadedDestinationFields)) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2">{t("mappingEditor.state.loadingConfiguration")}</span>
        </div>
      </div>
    )
  }

  // Show error state
  if (error && !config) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">{t("mappingEditor.errors.loadTitle")}</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
          >
            {t("common.actions.retry")}
          </button>
        </div>
      </div>
    )
  }

  // Don't render until we have both config and available destination fields
  if (!config || !hasLoadedDestinationFields) {
    return null
  }

  return (
    <div className="p-4">
      <ToolHeader
        title={t("mappingEditor.header.title", { name: mappingName })}
        description={t("mappingEditor.header.description", {
          source: config.atsEntityType,
          name: mappingName,
          entities: t((config.entities.length === 1 ? "mappingEditor.header.entitiesOne" : "mappingEditor.header.entitiesOther"), { count: n(config.entities.length) }),
          mapped: n(allMappings.filter((m) => m.isMapped).length),
        })}
        actions={
          <>
            <ToolbarPill onClick={() => setPanel("entities")} icon={<Boxes className="h-3.5 w-3.5" />} title={t("mappingEditor.toolbar.entitiesTitle")}>
              {t("mappingEditor.toolbar.entities", { count: n(config.entities.length) })}
            </ToolbarPill>
            <ToolbarPill onClick={() => setPanel("testing")} icon={<FlaskConical className="h-3.5 w-3.5" />} title={t("mappingEditor.toolbar.testTitle")}>
              {t("mappingEditor.toolbar.test")}
            </ToolbarPill>
            <ToolbarPill onClick={() => setPanel("json")} icon={<Braces className="h-3.5 w-3.5" />} title={t("mappingEditor.toolbar.jsonTitle")}>
              {t("mappingEditor.toolbar.json")}
            </ToolbarPill>
            <ToolbarPill onClick={() => setFlowOpen(true)} icon={<Waypoints className="h-3.5 w-3.5" />} title={t("mappingEditor.toolbar.flowTitle")}>
              {t("mappingEditor.toolbar.flow")}
            </ToolbarPill>
            <ToolbarPill variant="primary" onClick={handleSave} disabled={hasInvalidMappings} icon={<Save className="h-3.5 w-3.5" />} title={hasInvalidMappings ? t("mappingEditor.toolbar.saveBlockedTitle") : undefined}>
              {t("common.actions.save")}
            </ToolbarPill>
          </>
        }
      />

      <DestinationFieldMappingsTab
        allMappings={allMappings}
        hasInvalidMappings={hasInvalidMappings}
        config={config}
        onConfigChange={setConfig}
        atsInfoState={atsInfoState}
        onSave={handleSave}
      />

      {flowOpen && (
        <MappingFlowBlade open onClose={() => setFlowOpen(false)} mappingName={mappingName} primaryEntity={config.atsEntityType} mappings={allMappings} />
      )}

      <Blade open={panel === "entities"} onClose={() => setPanel(null)} size="lg" eyebrow={mappingName} title={t("mappingEditor.blades.entitiesTitle")} description={t("mappingEditor.blades.entitiesDescription")}>
        <EntityManagementTab config={config} onConfigChange={setConfig} />
      </Blade>

      <Blade open={panel === "testing"} onClose={() => setPanel(null)} size="xl" eyebrow={mappingName} title={t("mappingEditor.blades.testingTitle")} description={t("mappingEditor.blades.testingDescription")}>
        <TestingTab allMappings={allMappings} entityTypes={entityTypes} hasInvalidMappings={hasInvalidMappings} config={config} testForm={testForm} setTestForm={setTestForm} />
      </Blade>

      <Blade open={panel === "json"} onClose={() => setPanel(null)} size="lg" eyebrow={mappingName} title={t("mappingEditor.blades.jsonTitle")} description={t("mappingEditor.blades.jsonDescription")}>
        <JsonTab config={config} onConfigChange={setConfig} hasInvalidMappings={hasInvalidMappings} onSave={handleSave} />
      </Blade>
    </div>
  )
}

